/**
 * WebForm Component.
 * Dynamic Form will be generated as per Response from API.
 * The `WebForm` Component internally calls form fields components.
 * Field Component located in `public/js/contactUs/components/form`.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'kepler';
import _ from 'lodash';

// Components and Shapes.
import { Link } from '../common/index';
import { TextBox, SelectBox, RadioGroup, TextArea, DateSelector, Notes, FormButtons } from '../form/index';
import * as shape from '../../constants/PropTypesShapes';

class WebForm extends Component {

  // Set State to Validate all the Field and Field Configuration Field.
  constructor(props) {
    super(props);

    this.state = {
      validateField: false,
      fields: props.fields,
    };

    // Binding the Prop Methods.
    this.setFieldValue = this.setFieldValue.bind(this);
  }

  // Focus the First Field, after Component rendered.
  componentDidMount() {
    const firstFieldId = _.get(this.state, 'fields[0].id', '');

    if (firstFieldId && document.getElementById(firstFieldId)) {
      document.getElementById(firstFieldId).focus();
    }
  }

  // Props Value will be supplied after every Topic Tree Changes.
  componentWillReceiveProps(props) {
    this.setState({
      fields: props.fields,
    });
  }

  // Set the Field Value to Form State data.
  // Preserve the Field Value.
  setFieldValue(field) {
    const fieldIndex = _.findIndex(this.state.fields, { id: field.id });
    const formData = this.state.fields;

    if (formData[fieldIndex]) {
      if (formData[fieldIndex].value) {
        formData[fieldIndex].value = field.value;
      }

      if (formData[fieldIndex].hasError) {
        formData[fieldIndex].hasError = field.hasError;
      }
    }

    this.setState({
      fields: formData,
    });

    this.props.handleFieldValue(formData);

    if (this.props.submitOnFieldChange) {
      this.props.handleSubmit(this.state.fields);
    }
  }

  // Filter the Field Value By Field Id to get the Value.
  getFieldValue(fieldId) {
    let fieldValue = '';
    const fieldIndex = _.findIndex(this.state.fields, { id: fieldId });

    if (this.state.fields[fieldIndex]) {
      fieldValue = this.state.fields[fieldIndex].value;
    }

    return fieldValue;
  }

  // Return the Field Component as per `Field.Type`.
  // Text and Number are same currently.
  getField(field) {
    switch (field.type) {
      case 'text' :
      case 'number' :
        return (<TextBox
          key={field.id}
          options={field}
          validate={this.state.validateField}
          handleValue={this.setFieldValue}
          errorWarn={this.props.errors}
        />);
      case 'date' :
        return (<DateSelector
          key={field.id}
          options={field}
          format={this.props.dateFormat}
          validate={this.state.validateField}
          handleValue={this.setFieldValue}
          errorWarn={this.props.errors}
        />);
      case 'textarea' :
        return (<TextArea
          key={field.id}
          options={field}
          validate={this.state.validateField}
          handleValue={this.setFieldValue}
          errorWarn={this.props.errors}
        />);
      case 'select' :
        return (<SelectBox
          key={field.id}
          options={field}
          validate={this.state.validateField}
          handleValue={this.setFieldValue}
          errorWarn={this.props.errors}
        />);
      case 'radio' :
        return (<RadioGroup
          key={field.id}
          options={field}
          handleValue={this.setFieldValue}
        />);
      case 'paragraph' :
        return (<Notes
          key={field.id}
          label={field.label}
        />);
      case 'anchor' :
        return (<Link
          classNames="link-field"
          key={field.id}
          id={field.id}
          href={field.href}
          label={field.title}
          title={field.label}
          target="_blank"
        />);
      case 'button' :
        return (<FormButtons
          submitLabel={field.submitLabel}
          resetLabel={field.resetLabel}
          hasCancelButton={field.hasCancelButton}
          submitButtonStyle={field.submitButtonStyle}
          handleCancel={field.handleCancel}
        />);
      default:
        return <div />;
    }
  }

  // Iterate each Form Fields from State.
  getFormFields() {
    return this.state.fields.map(field => this.getField(field));
  }

  // Return Form Submit and Cancel Buttons, if Submit Label exist.
  getFormButtons() {
    if (this.props.submitLabel) {
      return (
        <FormButtons
          submitLabel={this.props.submitLabel}
          resetLabel={this.props.resetLabel}
          handleCancel={this.props.handleCancel}
        />
      );
    }
    return null;
  }

  // Handle the Form Validation and Submission.
  // Submit the Form, if there is no error.
  // Check if there is any errors in Form Fields to Trigger all the Form Field Validation to show the erros.
  handleFormValidate(e) {
    e.preventDefault();

    const errorFields = _.filter(this.state.fields, (field) => {
      const nonFields = ['paragraph', 'anchor'];
      return (field.hasError === true && nonFields.indexOf(field.type) === -1);
    });

    if (_.isEmpty(errorFields)) {
      this.props.handleSubmit(this.state.fields);
    } else {
      this.setState({
        validateField: true,
      });
    }
  }

  // Render the `WebForm` with Fields, Submit and Cancel Buttons.
  render() {
    return (
      <div>
        <Form onSubmit={e => this.handleFormValidate(e)}>
          {this.getFormFields()}
          {this.getFormButtons()}
        </Form>
      </div>
    );
  }

}

// WebForm Props Types.
WebForm.propTypes = {
  // Simple Prop Types.
  fields: PropTypes.arrayOf(PropTypes.shape(shape.WEBFORM_FIELDS_DETAILS)),
  dateFormat: PropTypes.string,
  submitOnFieldChange: PropTypes.bool,
  submitLabel: PropTypes.string,
  resetLabel: PropTypes.string,
  errors: PropTypes.shape(shape.WEBFORM_ERRORS),

  // Method Prop Types.
  handleFieldValue: PropTypes.func.isRequired,
  handleCancel: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
};

// WebForm Props default values.
WebForm.defaultProps = {
  fields: [],
  dateFormat: 'MM/DD/YYYY',
  submitOnFieldChange: false,
  submitLabel: '',
  resetLabel: '',
  errors: {},
  handleCancel: () => {},
};

export default WebForm;



// WEBPACK FOOTER //
// ./contactUs/components/common/WebForm.js